import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { X, Search, MessageCircle, Gift, Crown, Sparkles, TrendingUp, Settings, Coins, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/App";

export const AdeHubMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { t } = useLang();
  const { isDark } = useTheme();
  const [uncoverCount, setUncoverCount] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    api.get("/gifts/uncover/count")
      .then((r) => setUncoverCount(r.data.count || 0))
      .catch(() => {});
  }, [isOpen]);

  if (!isOpen) return null;

  const items = [
    { label: t("hubExplore"), desc: t("hubExploreDesc"), icon: Search, color: "#FFB020", g1: "#FFB84D", g2: "#FF7A00", path: "/app/explore", testId: "hub-explore" },
    { label: t("hubMessages"), desc: t("hubMessagesDesc"), icon: MessageCircle, color: "#A855F7", g1: "#C084FC", g2: "#7C3AED", path: "/app/messages", testId: "hub-messages" },
    { label: t("hubCredits"), desc: t("hubCreditsDesc"), icon: Coins, color: "#F5C242", g1: "#FF9F5A", g2: "#FF4D2E", path: "/app/credits", testId: "hub-credits" },
    { label: t("hubGifts"), desc: t("hubGiftsDesc"), icon: Gift, color: "#FF3B5C", g1: "#FF6FA5", g2: "#E91E63", path: "/app/gift-center", testId: "hub-gifts", badge: uncoverCount },
    { label: t("hubGoldenRing"), desc: t("hubGoldenRingDesc"), icon: Crown, color: "#D4AF37", g1: "#F7CE55", g2: "#E8A317", path: "/app/golden-ring", testId: "hub-golden-ring" },
    { label: t("hubCreator"), desc: t("hubCreatorDesc"), icon: TrendingUp, color: "#10B981", g1: "#2DD4BF", g2: "#0D9488", path: "/app/creator-dashboard", testId: "hub-creator" },
    { label: t("hubSettings"), desc: t("hubSettingsDesc"), icon: Settings, color: "#94A3B8", g1: "#93A5FD", g2: "#4F46E5", path: "/app/settings", testId: "hub-settings" },
  ];

  const go = (path) => {
    onClose();
    navigate(path);
  };

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 2000 }} data-testid="ade-hub-menu">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
        data-testid="hub-backdrop"
      />

      {/* Bottom Sheet — neon glass con aurora videogame */}
      <div className="hub-sheet absolute bottom-0 left-0 right-0 rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 safe-bottom max-h-[85dvh] overflow-y-auto" style={{ background: isDark ? "#0B0B10" : "#FFFFFF" }}>
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className={`w-10 h-1 rounded-full ${isDark ? "bg-white/20" : "bg-gray-200"}`} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-2 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 flex items-center justify-center overflow-hidden">
              <img src="/images/ade-logo-extracted.png" alt="ADE" className="w-full h-full object-cover scale-[1.7]" />
            </div>
            <div>
              <h2 className="text-lg font-bold">
                <span className={isDark ? "text-white" : "text-gray-900"}>Menu </span>
                <span className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 bg-clip-text text-transparent">ADE</span>
              </h2>
              <p className={`text-[10px] tracking-wide ${isDark ? "text-white/40" : "text-gray-400"}`}>{t("hubSubtitle")}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2.5 rounded-full transition-colors ${isDark ? "bg-white/10 hover:bg-white/20" : "bg-gray-100 hover:bg-gray-200"}`}
            data-testid="hub-close-btn"
          >
            <X className={`w-4 h-4 ${isDark ? "text-white/70" : "text-gray-500"}`} />
          </button>
        </div>

        {/* Grid of app-style squircle icons (original look) */}
        <div className="grid grid-cols-4 gap-x-2 gap-y-5 px-5 pb-8">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => go(item.path)}
                className="flex flex-col items-center gap-2 active:scale-95 transition-transform"
                data-testid={item.testId}
              >
                <div
                  className="hub-icon relative w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${item.g1}, ${item.g2})`,
                    boxShadow: `0 5px 14px ${item.g2}45, inset 0 1px 1px rgba(255,255,255,0.45), inset 0 -6px 12px rgba(0,0,0,0.22)`,
                  }}
                >
                  <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                  {item.badge > 0 && (
                    <span data-testid="hub-gifts-badge"
                      className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 rounded-full bg-amber-500 text-white text-[11px] font-black flex items-center justify-center ring-2 ring-white/80">
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-xs font-medium leading-tight text-center ${isDark ? "text-white/80" : "text-gray-700"}`}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};
